import formatDollars from 'utils/format-dollars';
import { DataGrid, DataGridComponents, createColumnHelper, useDefaultTable } from 'foundation';
import React, { useMemo } from 'react';
import { TransformedConsumptionBreakdownItem } from 'types/consumption';
import { useSession } from 'store';
import { BillingMethod, PlanType } from 'entities/tenant';

const columnHelper = createColumnHelper<TransformedConsumptionBreakdownItem>();

export const ConsumptionReportsTable = ({
  breakdown,
  totalBreakdownCount,
  isLoading,
  searchFilter,
  setSearchFilter,
  pagination,
  setPagination,
  setSorting,
  sorting,
}) => {
  const session = useSession();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Instance',
      }),
      columnHelper.accessor('dbid', {
        header: 'ID',
      }),
      columnHelper.accessor('tier', {
        header: 'Tier',
        cell: cx => cx.getValue(),
      }),
      columnHelper.accessor('productName', {
        header: 'Product name',
        cell: cx => cx.getValue(),
      }),
      columnHelper.accessor('pricingPlan', {
        header: 'Pricing plan',
        cell: cx => cx.getValue(),
      }),
      columnHelper.accessor('billingStatus', {
        header: 'Billing status',
        cell: cx => cx.getValue(),
      }),
      columnHelper.accessor('usage', {
        header: 'Usage',
      }),
      columnHelper.accessor('unitOfMeasure', {
        header: 'Unit of measure',
        cell: cx => cx.getValue(),
      }),
      columnHelper.accessor('unitCostInDollars', {
        header: 'Unit price',
        enableHiding: true,
      }),
      columnHelper.accessor('cost', {
        id: 'self-serve-cost',
        header: 'Amount',
        cell: cx => formatDollars(cx.getValue()),
        enableResizing: false,
        enableHiding: true,
      }),
      columnHelper.accessor('cost', {
        id: 'enterprise-consumption',
        header: 'Credits consumed',
        enableResizing: false,
        enableHiding: true,
      }),
    ],
    [session.planType]
  );
  const table = useDefaultTable({
    data: breakdown,
    columns,
    initialState: {
      columnVisibility: {
        unitCostInDollars: session.tenant.planType === PlanType.SELF_SERVE,
        'self-serve-cost': session.tenant.billingMethod === BillingMethod.PAYG,
        'enterprise-consumption': session.tenant.billingMethod === BillingMethod.PREPAID,
      },
    },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableSorting: !isLoading,
    state: {
      globalFilter: searchFilter,
      pagination,
      sorting,
    },
    onGlobalFilterChange: setSearchFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    pageCount:
      totalBreakdownCount % pagination.pageSize === 0
        ? totalBreakdownCount / pagination.pageSize
        : Math.ceil(totalBreakdownCount / pagination.pageSize),
  });
  return (
    <DataGrid
      isResizable={true}
      isLoading={isLoading}
      tableInstance={table}
      styling={{
        headerStyle: 'clean',
      }}
      components={{
        Body: props => (
          <DataGridComponents.Body
            {...props}
            innerProps={{
              style: {
                borderTop: '1px solid rgb(var(--theme-palette-neutral-border-weak))',
              },
            }}
          />
        ),
        BodyRow: props => (
          <DataGridComponents.BodyRow
            {...props}
            innerProps={{
              style: {
                minHeight: '56px',
              },
            }}
          />
        ),
      }}
    />
  );
};
